import api from './api';
import { LabClinic, LabPatient, LabProtocol, LabResult, LabReferenceValue, LabTestType, LabProtocolStatus, LabSpecies } from '@/types';

// ==================== Lab Clinic Types ====================

export interface LabClinicCreate {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  contact_vet_name?: string;
  contact_vet_phone?: string;
  notes?: string;
}

export interface LabClinicUpdate extends Partial<LabClinicCreate> {
  is_active?: boolean;
}

export interface LabClinicFilters {
  search?: string;
  is_active?: boolean;
  skip?: number;
  limit?: number;
}

// ==================== Lab Patient Types ====================

export interface LabPatientCreate {
  name: string;
  species: string;
  breed?: string;
  sex?: string;
  birth_date?: string;
  color?: string;
  owner_name?: string;
  owner_phone?: string;
  lab_clinic_id?: number;
  referring_clinic_name?: string;
  referring_vet_name?: string;
  notes?: string;
}

export interface LabPatientUpdate extends Partial<LabPatientCreate> {
  is_active?: boolean;
}

export interface LabPatientFilters {
  search?: string;
  species?: string;
  lab_clinic_id?: number;
  is_active?: boolean;
  skip?: number;
  limit?: number;
}

// ==================== Lab Protocol Types ====================

export interface LabProtocolCreate {
  lab_patient_id: number;
  requesting_vet_name?: string;
  protocol_date: string;
  observations?: string;
}

export interface LabProtocolUpdate {
  requesting_vet_name?: string;
  protocol_date?: string;
  status?: LabProtocolStatus;
  observations?: string;
}

export interface LabProtocolFilters {
  patient_id?: number;
  status?: LabProtocolStatus;
  date_from?: string;
  date_to?: string;
  search?: string;
  skip?: number;
  limit?: number;
}

// ==================== Lab Result Types ====================

export interface LabResultCreate {
  test_type: LabTestType;
  parameter_code: string;
  parameter_name: string;
  result_value?: string;
  result_numeric?: number;
  reference_min?: number;
  reference_max?: number;
  reference_text?: string;
  unit?: string;
  notes?: string;
}

export interface LabResultUpdate {
  result_value?: string;
  result_numeric?: number;
  is_abnormal?: boolean;
  notes?: string;
}

// ==================== Lab Reference Value Types ====================

export interface LabReferenceValueCreate {
  test_type: LabTestType;
  parameter_code: string;
  parameter_name: string;
  species: string;
  reference_min?: number;
  reference_max?: number;
  reference_text?: string;
  unit?: string;
  display_order?: number;
}

export interface LabReferenceValueUpdate {
  reference_min?: number;
  reference_max?: number;
  reference_text?: string;
  unit?: string;
  display_order?: number;
  is_active?: boolean;
}

export interface LabReferenceValueFilters {
  species?: string;
  test_type?: LabTestType;
  is_active?: boolean;
}

// ==================== Lab Clinics Service ====================

export const labClinicsService = {
  async getClinics(filters?: LabClinicFilters): Promise<LabClinic[]> {
    const response = await api.get<LabClinic[]>('/lab/clinics', { params: filters });
    return response.data;
  },

  async getClinic(id: number): Promise<LabClinic> {
    const response = await api.get<LabClinic>(`/lab/clinics/${id}`);
    return response.data;
  },

  async createClinic(data: LabClinicCreate): Promise<LabClinic> {
    const response = await api.post<LabClinic>('/lab/clinics', data);
    return response.data;
  },

  async updateClinic(id: number, data: LabClinicUpdate): Promise<LabClinic> {
    const response = await api.put<LabClinic>(`/lab/clinics/${id}`, data);
    return response.data;
  },

  async deleteClinic(id: number): Promise<void> {
    await api.delete(`/lab/clinics/${id}`);
  },
};

// ==================== Lab Patients Service ====================

export const labPatientsService = {
  async getPatients(filters?: LabPatientFilters): Promise<LabPatient[]> {
    const response = await api.get<LabPatient[]>('/lab/patients', { params: filters });
    return response.data;
  },

  async getPatient(id: number): Promise<LabPatient> {
    const response = await api.get<LabPatient>(`/lab/patients/${id}`);
    return response.data;
  },

  async createPatient(data: LabPatientCreate): Promise<LabPatient> {
    const response = await api.post<LabPatient>('/lab/patients', data);
    return response.data;
  },

  async updatePatient(id: number, data: LabPatientUpdate): Promise<LabPatient> {
    const response = await api.put<LabPatient>(`/lab/patients/${id}`, data);
    return response.data;
  },

  async deletePatient(id: number): Promise<void> {
    await api.delete(`/lab/patients/${id}`);
  },
};

// ==================== Lab Protocols Service ====================

export const labProtocolsService = {
  async getProtocols(filters?: LabProtocolFilters): Promise<LabProtocol[]> {
    const response = await api.get<LabProtocol[]>('/lab/protocols', { params: filters });
    return response.data;
  },

  async getProtocol(id: number): Promise<LabProtocol> {
    const response = await api.get<LabProtocol>(`/lab/protocols/${id}`);
    return response.data;
  },

  async createProtocol(data: LabProtocolCreate): Promise<LabProtocol> {
    const response = await api.post<LabProtocol>('/lab/protocols', data);
    return response.data;
  },

  async createProtocolWithResults(data: LabProtocolCreate & { results: LabResultCreate[] }): Promise<LabProtocol> {
    const response = await api.post<LabProtocol>('/lab/protocols/with-results', data);
    return response.data;
  },

  async updateProtocol(id: number, data: LabProtocolUpdate): Promise<LabProtocol> {
    const response = await api.put<LabProtocol>(`/lab/protocols/${id}`, data);
    return response.data;
  },

  async updateProtocolStatus(id: number, status: LabProtocolStatus): Promise<LabProtocol> {
    const response = await api.put<LabProtocol>(`/lab/protocols/${id}/status`, null, {
      params: { new_status: status }
    });
    return response.data;
  },

  async deleteProtocol(id: number): Promise<void> {
    await api.delete(`/lab/protocols/${id}`);
  },
};

// ==================== Lab Results Service ====================

export const labResultsService = {
  async getResults(protocolId: number): Promise<LabResult[]> {
    const response = await api.get<LabResult[]>(`/lab/protocols/${protocolId}/results`);
    return response.data;
  },

  async createResult(protocolId: number, data: LabResultCreate): Promise<LabResult> {
    const response = await api.post<LabResult>(`/lab/protocols/${protocolId}/results`, data);
    return response.data;
  },

  async createResultsBulk(protocolId: number, results: LabResultCreate[]): Promise<LabResult[]> {
    const response = await api.post<LabResult[]>(`/lab/protocols/${protocolId}/results/bulk`, results);
    return response.data;
  },

  async updateResult(id: number, data: LabResultUpdate): Promise<LabResult> {
    const response = await api.put<LabResult>(`/lab/results/${id}`, data);
    return response.data;
  },

  async deleteResult(id: number): Promise<void> {
    await api.delete(`/lab/results/${id}`);
  },
};

// ==================== Lab Reference Values Service ====================

export const labReferenceValuesService = {
  async getReferenceValues(filters?: LabReferenceValueFilters): Promise<LabReferenceValue[]> {
    const response = await api.get<LabReferenceValue[]>('/lab/reference-values', { params: filters });
    return response.data;
  },

  async createReferenceValue(data: LabReferenceValueCreate): Promise<LabReferenceValue> {
    const response = await api.post<LabReferenceValue>('/lab/reference-values', data);
    return response.data;
  },

  async updateReferenceValue(id: number, data: LabReferenceValueUpdate): Promise<LabReferenceValue> {
    const response = await api.put<LabReferenceValue>(`/lab/reference-values/${id}`, data);
    return response.data;
  },

  async deleteReferenceValue(id: number): Promise<void> {
    await api.delete(`/lab/reference-values/${id}`);
  },

  async seedCanineValues(): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/lab/reference-values/seed-canine');
    return response.data;
  },
};

// ==================== Lab Species ====================
export interface LabSpeciesFilters {
  search?: string;
  is_active?: boolean;
}

export type LabSpeciesCreate = {
  name: string;
  scientific_name?: string;
  description?: string;
};

export type LabSpeciesUpdate = Partial<LabSpeciesCreate> & {
  is_active?: boolean;
};

export const labSpeciesService = {
  async getAll(filters?: LabSpeciesFilters) {
    const response = await api.get<LabSpecies[]>('/lab/species', { params: filters });
    return response.data;
  },
  async create(data: LabSpeciesCreate) {
    const response = await api.post<LabSpecies>('/lab/species', data);
    return response.data;
  },
  async update(id: number, data: LabSpeciesUpdate) {
    const response = await api.put<LabSpecies>(`/lab/species/${id}`, data);
    return response.data;
  },
  async delete(id: number) {
    await api.delete(`/lab/species/${id}`);
  },
};
